import type {ImageItem} from '@PosterWhiteboard/items/image-item/image-item.class';
import type {VideoItem} from '@PosterWhiteboard/items/video-item/video-item.class';
import type {ImageSlideItem} from '@PosterWhiteboard/items/slideshow-item/slide-items/image-slide-item.class';
import type {VideoSlideItem} from '@PosterWhiteboard/items/slideshow-item/slide-items/video-slide-item.class';
import type {ItemData} from '@PosterWhiteboard/page/add-item.class';
import type {ALIGNMENT_TYPE} from '@Components/dropdown-v2/dropdown.types';

/** Top level of the media menu. Every picker splits into these two before offering a source. */
export enum MediaSourceGroup {
  IMAGE = 'image',
  VIDEO = 'video',
}

/** Second level: where the media comes from. */
export enum MediaSourceKind {
  UPLOADS = 'uploads',
  STOCK = 'stock',
  AI = 'ai',
}

/**
 * What picking a leaf should DO. The menu shape is identical across surfaces but the mutation is
 * not — appending a background to the timeline sequence, setting the page background, and swapping
 * the selected media are three different writes, so intent travels with every request.
 */
export enum MediaSourceIntent {
  APPEND_BACKGROUND = 'append-background',
  SET_BACKGROUND = 'set-background',
  REPLACE_BACKGROUND = 'replace-background',
  REPLACE_ITEM = 'replace-item',
}

/**
 * One selectable option: a source, usually crossed with a group. `group: null` means the pick spans
 * media types — the top-level Uploads shortcut, whose modal lists image AND video backgrounds.
 * Only UPLOADS supports a group-less pick; stock and AI always name a media type.
 */
export interface MediaSourceLeaf {
  group: MediaSourceGroup | null;
  kind: MediaSourceKind;
}

/** What is being replaced. Decides the replace menu's AI entries and the stock panel's tab order. */
export enum ReplaceMediaKind {
  IMAGE = 'image',
  VIDEO = 'video',
  STICKER = 'sticker',
  ICON = 'icon',
  AUDIO = 'audio',
}

export type ReplaceAiPanelKey = 'ai-images' | 'ai-videos' | 'ai-stickers' | 'ai-voice';

/**
 * A pick from the flat replace menu. Unlike MediaSourceLeaf there is no media-type group — the
 * item being replaced names its own type, and the AI rows carry the exact panel they open.
 */
export type ReplaceSourceSelection = {kind: MediaSourceKind.UPLOADS} | {kind: MediaSourceKind.STOCK} | {kind: MediaSourceKind.AI; aiPanel: ReplaceAiPanelKey};

/** The per-surface class names the roomier thumbnail rows need; each surface owns its own SCSS module. */
export interface ReplaceRowThumbnailStyling {
  thumbnailClasses: string;
  /** Applied to non-AI icons only; AI icons always take the AI tint. */
  iconClasses: string;
}

/** One flat replace-menu row, ready for a surface to map onto its own row style. */
export interface ReplaceSourceOption {
  id: string;
  icon: string;
  text: string;
  /** Sub-text for the roomier mobile/popup rows; compact dropdown rows ignore it. */
  subText: string;
  isAi: boolean;
  onSelect: () => void;
}

export type ReplaceableMediaItem = ImageItem | ImageSlideItem | VideoItem | VideoSlideItem;

/**
 * A resolved pick, ready for the dispatcher. Item replacement is the only intent that needs a
 * target and a write-back callback, so it carries them and the background intents cannot.
 */
export type MediaSourceRequest =
  | {
      intent: MediaSourceIntent.APPEND_BACKGROUND | MediaSourceIntent.SET_BACKGROUND;
      leaf: MediaSourceLeaf;
    }
  | {
      intent: MediaSourceIntent.REPLACE_BACKGROUND;
      selection: ReplaceSourceSelection;
    }
  | {
      intent: MediaSourceIntent.REPLACE_ITEM;
      selection: ReplaceSourceSelection;
      item: ReplaceableMediaItem;
      onReplace: (itemData: ItemData) => void;
    };

/** A leaf as the renderers need it: display data plus the click that dispatches it. */
export interface MediaSourceOption {
  leaf: MediaSourceLeaf;
  id: string;
  text: string;
  icon: string;
  /** Sub-text for the roomier flat rows; the compact flyout rows ignore it. */
  subText: string;
  isAi: boolean;
  onSelect: () => void;
}

/** How a group row is drawn. Each surface keeps whatever row style its other options already use. */
export enum MediaSourceRowStyle {
  /** Icon + label + caret. Matches the right panel and timeline popups. */
  COMPACT = 'compact',
  /** Thumbnail tile + label + sub-text. Matches the add-item bar's roomier rows. */
  DETAILED = 'detailed',
}

/** One `Image ▸` / `Video ▸` branch. */
export interface MediaSourceGroupNode {
  group: MediaSourceGroup;
  id: string;
  text: string;
  icon: string;
  /** Only drawn by the DETAILED row style. */
  subText: string;
  options: MediaSourceOption[];
}

export interface BuildMediaSourceGroupsOptions {
  /** Defaults to both groups. */
  groups?: MediaSourceGroup[];
  /** Defaults to all three kinds. Drop UPLOADS where a top-level Uploads shortcut already covers it. */
  kinds?: MediaSourceKind[];
  onSelect: (leaf: MediaSourceLeaf) => void;
}

export interface BuildReplaceSourceOptionsParams {
  idPrefix: string;
  onSelect: (selection: ReplaceSourceSelection) => void;
}

export const MEDIA_SOURCE_FLYOUT_HOVER_DELAY_MS = 300;

/** The design-system class that tints AI affordances. */
export const AI_CONTENT_CLASS = 'content-ai';

export interface MediaSourceMenuProps {
  groups: MediaSourceGroupNode[];
  /**
   * Flyout-less leaf rows rendered above the group rows, in the menu's row style — the top-level
   * Uploads shortcut (see buildTopLevelUploadsOption). Not part of `groups`: every group renders as
   * a branch with a flyout, and these are direct picks.
   */
  leadingOptions?: MediaSourceOption[];
  /**
   * Classes for the leading rows. Defaults to a grid-2 bottom margin separating the Uploads
   * shortcut from the group rows; pass spacing-m-b-0 for a flush menu.
   */
  leadingOptionClassName?: string;
  /**
   * Which side the group flyouts open on. Defaults to opening leftward, which suits the right-hand
   * editing panel; surfaces anchored on the left of the screen pass RIGHT_START.
   */
  flyoutAlignment?: ALIGNMENT_TYPE;
  /** Defaults to COMPACT. */
  rowStyle?: MediaSourceRowStyle;
  /**
   * Draws a pointer on the flyout aimed back at its row, so it reads as belonging to Image or Video
   * rather than to the menu as a whole. Off by default; the add-item bar's popups all use one.
   */
  showFlyoutArrow?: boolean;
}
